// START IMPORTS
// Theme and UI imports
import {
  useAppConfigContext,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { useReport, useSeonData } from '@ltvco/refresh-lib/hooks';
import {
  useDarkWebUpsell,
  ReactivationDaysLeft,
  uncancelBillingVariation2Value,
  uncancelBillingPbrFeatureName,
  uncancelBillingControlValue,
} from '@ltvco/refresh-lib/payments';
import { Box, CustomIcons, Stack } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  DebugMenu,
  EmailFraudCheckSection,
  EmailReport as EmailReportType,
  ReportFactory,
  ReportLoading,
  ReportMonitor,
  ReportOptions,
  useDarkWebReport,
  useSession,
  SerializedPerson,
  SerializedDarkWebBreach,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  EmailSection as EmailSectionV2,
  PhoneSection as PhoneSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  EducationSection as EducationSectionV2,
  JobsSection as JobsSectionV2,
  SocialSection as SocialSectionV2,
  ReportActionsWithDateUpdated,
  ReportRating as ReportRatingV2,
  NotesSection as NotesSectionV2,
  RelatedReportsSection as RelatedReportsSectionV2,
  AssetsSection as AssetsSectionV2,
  PotentialOwners as PotentialOwnersV2,
  FeatureBlockedModal,
  IReportMenuItemWithPermissions,
  ReportNavigationMobileMenu,
  ReportNavigationMenuWithPermissions,
  EmailDarkWebVerify,
  EmailDarkWebLoadingScreen,
  EmailDarkWebNoBreaches,
  EmailDarkWebResults,
  ReportSection,
  ReportNavigationMenuProps,
  NavLinkDataV2,
  useFeatureBlockedModal,
  LizanoListItemDataEmail,
  EmailReportOverview,
  ReportNullState,
  ReportClaimingSection,
  ReportGeneratingScreen,
  MonitoringCard,
  AssociatedAccountsSection,
  getAccountsFoundCount,
  ReportChangesOverview,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useFeatureIsOn, useFeatureValue } from '@ltvco/refresh-lib/vendors';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getEmailNavLinkDataV2 } from 'navLinkData/emailNavLinkData';

import { getHighConfidenceDisplayData } from './utils/getHighConfidenceDisplayData';
import { getReportSections } from './utils/getReportSections';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface EmailReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'email';
// END CONSTANTS

function EmailDarkWebError({ email }: { email: string }) {
  return (
    <ReportSection
      dataCy="dark-web-email-section"
      title="Possible Data Breaches"
      titleIcon={<CustomIcons.DarkWebIcon fontSize="small" sx={{ mr: 1 }} />}
      sectionId="dark-web-email-section"
      hasContent={false}
      reportTitle={email}
      customNullText="Something went wrong processing this section."
    />
  );
}

export function EmailReport({
  permalink,
  firstReportScreen,
}: EmailReportProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;

  const {
    session: { account },
  } = useSession();
  const { isHidden } = useSubscriptionAccessConfigContext();
  const { trackEventGA4 } = useAppConfigContext();
  const {
    isFeatureBlockedModalOpen,
    openFeatureBlockedModal,
    closeFeatureBlockedModal,
  } = useFeatureBlockedModal();

  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const { isDarkWebBlocked } = useDarkWebUpsell();
  const uncancelFeatureValue = useFeatureValue(
    uncancelBillingPbrFeatureName,
    uncancelBillingControlValue
  );
  const uncancelFeatureIsOn = useFeatureIsOn(uncancelBillingPbrFeatureName);
  const [, triggerUpdate] = useState(0);
  const currentUserInfo = account?.account?.user_info;

  const zeroed = isZeroed(queryResult);
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);

  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: true,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [isDarkWebSectionLoading, setIsDarkWebSectionLoading] = useState(false);
  const [showDarkWebEmailSection, setShowDarkWebEmailSection] = useState(false);
  const [reportUpdateDate, setUpdateDate] = useState<string | undefined>();
  const [report, setReport] = useState<EmailReportType | null>(null);
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const date = new DateUtil();
  const {
    data: darkWebResults,
    isLoading: isDarkWebReportLoading,
    isError: isDarkWebReportError,
  } = useDarkWebReport({
    email: report?.emailAddress as string,
  });

  const darkWebBreaches = darkWebResults?.data?.breaches ?? [];
  const breachUpdateDate = darkWebResults?.data?.rawData?.meta?.updated_at;
  const isEmailPartialAddress = useFeatureIsOn('OAR-1706');
  const isInHighConfidenceSectionsExperiment = useFeatureIsOn('RFRSH-4240');
  const isInSeonDataSectionVariation = useFeatureIsOn('RFRSH-4465');

  const { data: seonData, isLoading: isSeonDataLoading } = useSeonData(
    { email: report?.emailAddress ?? '' },
    { enabled: isInSeonDataSectionVariation }
  );
  // END DERIVED_DATA_AND_UTILS

  // START COMPUTED_VALUES
  const revealDarkWebEmailSection =
    showDarkWebEmailSection && !isDarkWebSectionLoading;

  const delayDarkWebLoading = 1000;
  // END COMPUTED_VALUES

  // START EVENT_HANDLERS
  function handleDarkWebSectionLoadingScreen() {
    setIsDarkWebSectionLoading(true);
    setTimeout(() => {
      setIsDarkWebSectionLoading(false);
      setShowDarkWebEmailSection(true);
    }, delayDarkWebLoading * 10);
  }
  // END EVENT_HANDLERS

  // START EFFECTS
  useEffect(() => {
    setActiveClaiming(
      currentMonitors.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      // The flattenedRemouladeResult already handles the nested data structure
      // Both REMO and regular useReport data are now at queryResult.data
      setReport(ReportFactory.create(queryResult.data, 'email'));
      setShowDarkWebEmailSection(false);
    }
  }, [queryResult.data, queryResult.isSuccess]);

  useEffect(() => {
    const remoUpdateDate = currentMonitors?.find(
      (monitor) => monitor.permalink === permalink
    )?.updated_at;
    const coreUpdateDate = report?.data?.meta?.updated_at;

    if (reportIsMonitored && remoUpdateDate) {
      setUpdateDate(date.parseDateFromString(remoUpdateDate, 'yyyy-MM-dd'));
    } else if (coreUpdateDate) {
      setUpdateDate(
        date.parseDateFromString(
          coreUpdateDate,
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss ZZZ'
        )
      );
    }
  }, [
    report?.data?.meta?.updated_at,
    currentMonitors,
    permalink,
    reportIsMonitored,
  ]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="email"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={10} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  if (!report) {
    return <ReportLoading menuItems={10} />;
  }

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const showReportClaiming =
    currentUserInfo?.email?.toLowerCase() ===
    report?.emailAddress?.toLowerCase();

  const ClaimingComponent = showReportClaiming && (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  );

  const MonitoringComponent = (
    <MonitoringCard permalink={permalink} reportType={reportType} />
  );

  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    relatives,
  } = owner;

  const { supportingEmail } = report;
  const highConfidenceThreshold =
    reportOptions.higherConfidenceThreshold ||
    constants.config.higherConfidenceThreshold;
  const showHighConfidenceDataOnly = reportOptions.showHighConfidenceDataOnly;
  const { phonesForDisplay, emailsForDisplay, addressesForDisplay } =
    getHighConfidenceDisplayData({
      owner,
      showHighConfidenceDataOnly,
      highConfidenceThreshold,
    });
  const phoneItems = isInHighConfidenceSectionsExperiment
    ? phonesForDisplay
    : phones;
  const emailItems = isInHighConfidenceSectionsExperiment
    ? emailsForDisplay
    : emails;
  const addressItems = isInHighConfidenceSectionsExperiment
    ? addressesForDisplay
    : addresses;

  const { fraud_score, deliverability, recent_abuse, is_leaked } =
    supportingEmail || {};

  const associatedAccountsCount = isSeonDataLoading
    ? null
    : seonData != null
    ? getAccountsFoundCount(seonData.email_details?.account_details)
    : null;

  const emailNavLinkDataV2 = getEmailNavLinkDataV2(
    report?.data.people?.length || 0,
    owner,
    isInHighConfidenceSectionsExperiment
      ? {
          phones: phoneItems,
          emails: emailItems,
          addresses: addressItems,
        }
      : undefined,
    associatedAccountsCount,
    isSeonDataLoading
  ) as ReportNavigationMenuProps['menuItems'];

  const DARK_WEB_NAV_ITEM_NAME = 'dark-web-email-section';
  const navMenuItemsWithPermissions = (
    emailNavLinkDataV2 as IReportMenuItemWithPermissions[]
  ).map((item) =>
    item.name === DARK_WEB_NAV_ITEM_NAME
      ? {
          ...item,
          isLocked: isDarkWebBlocked,
          onLockedClick: () => {
            trackEventGA4({
              event_name: 'engagement_click',
              type: 'darkwebscan_reportsidebar',
              section_name: 'darkweb_upsell',
            });
            openFeatureBlockedModal();
          },
        }
      : item
  );

  const queryParams = new URLSearchParams(search);
  const fallbackEmail = queryParams.get('email') || '';
  const lowerCaseEmailAddress =
    report?.emailAddress?.toLowerCase() ?? fallbackEmail;
  document.title = `${lowerCaseEmailAddress} - BeenVerified`;

  const buildReportDataForListV2 = () => {
    const emailType =
      emails.find(
        (emailObj) => emailObj.address === lowerCaseEmailAddress || {}
      )?.type || null;
    const data: LizanoListItemDataEmail = {
      permalink,
      email: lowerCaseEmailAddress || '',
      type: emailType || 'Personal',
    };

    if (report?.data?.people && report?.data?.people?.length) {
      data.owners = report?.data?.people.map((owner: SerializedPerson) => {
        return {
          first_name: owner?.identity?.names?.[0]?.parsed?.first || '',
          last_name: owner?.identity?.names?.[0]?.parsed?.last || '',
        };
      });
    }

    return data;
  };
  const alternativeTitle = isEmailPartialAddress
    ? 'Possible Locations'
    : undefined;

  const potentialOwners = report.data.people;

  const reportSections = getReportSections(
    emailNavLinkDataV2 as NavLinkDataV2[]
  );
  // END DATA_PREPARATION
  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenuWithPermissions
            reportType="Email Report"
            headerTitle={lowerCaseEmailAddress}
            menuItems={navMenuItemsWithPermissions}
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {/* START REPORT_CHANGES_OVERVIEW */}
          {reportIsMonitored && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={emailNavLinkDataV2}
            />
          )}
          {/* END REPORT_CHANGES_OVERVIEW */}

          {/* START REPORT_ACTIONS */}
          <>
            <Box
              sx={{
                height: 30,
                backgroundColor: '#f8f8f8',
                position: 'sticky',
                marginBottom: '-10px',
                marginX: -1,
                top: 52,
                zIndex: 10,
                display: {
                  xs: 'none',
                  sm: 'block',
                  md: 'block',
                  lg: 'block',
                },
              }}
            />
            {uncancelFeatureIsOn &&
              uncancelFeatureValue === uncancelBillingVariation2Value && (
                <ReactivationDaysLeft
                  direction="row"
                  isInReport
                  customPadding={'16px 0 8px 0'}
                />
              )}
            <ReportActionsWithDateUpdated
              reportType={reportType}
              reportTitle="Email"
              dateUpdated={reportUpdateDate}
              permalink={permalink}
              navLinkData={emailNavLinkDataV2 as unknown as NavLinkDataV2[]}
              reportUpgraded={false}
              reportDataForListV2={
                isInListV2Variation ? buildReportDataForListV2() : undefined
              }
            />
          </>
          {/* END REPORT_ACTIONS */}

          {/* START REPORT_OVERVIEW */}
          <EmailReportOverview
            email={lowerCaseEmailAddress}
            showHighConfidenceToggle
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) => {
              handleSetReportOptions({
                showHighConfidenceDataOnly: value,
              });
            }}
            personName={ownerName || lowerCaseEmailAddress}
            emailOverviewItemsData={{
              emails,
              jobs,
              educations,
              phones,
              profiles,
              usernames,
              peopleCount: report.data.people?.length,
            }}
            reportType={reportType}
            dateUpdated={reportUpdateDate}
            Monitoring={MonitoringComponent}
          />
          {/* END REPORT_OVERVIEW */}

          {ClaimingComponent}

          {/* START FRAUD_CHECK_SECTION */}
          <EmailFraudCheckSection
            fraudScore={fraud_score}
            deliverability={deliverability}
            abusiveBehavior={recent_abuse}
            leaked={is_leaked}
            showEmailFraudScan={supportingEmail != undefined}
            emailAddress={lowerCaseEmailAddress}
            showReportCTA={!isHidden('fraud_scan')}
          />
          {/* END FRAUD_CHECK_SECTION */}

          {isInSeonDataSectionVariation && (
            <AssociatedAccountsSection
              personName={ownerName || lowerCaseEmailAddress}
              emailDetails={seonData?.email_details}
              isLoading={isSeonDataLoading}
              onEmailReport
            />
          )}

          {/* START DARK_WEB_SECTION */}
          {!isDarkWebBlocked && (
            <>
              {revealDarkWebEmailSection ? (
                isDarkWebReportLoading ? (
                  <EmailDarkWebLoadingScreen
                    email={lowerCaseEmailAddress}
                    delayDuration={delayDarkWebLoading}
                  />
                ) : isDarkWebReportError ? (
                  <EmailDarkWebError
                    email={lowerCaseEmailAddress ?? ownerName}
                  />
                ) : darkWebBreaches.length > 0 ? (
                  <EmailDarkWebResults
                    email={lowerCaseEmailAddress ?? ownerName}
                    breaches={darkWebBreaches as SerializedDarkWebBreach[]}
                    lastUpdated={breachUpdateDate ?? ''}
                    permalink={permalink}
                  />
                ) : (
                  <EmailDarkWebNoBreaches
                    email={lowerCaseEmailAddress ?? ownerName}
                    lastUpdated={breachUpdateDate ?? ''}
                    permalink={permalink}
                  />
                )
              ) : (
                <>
                  {isDarkWebSectionLoading ? (
                    <EmailDarkWebLoadingScreen
                      email={lowerCaseEmailAddress}
                      delayDuration={delayDarkWebLoading}
                    />
                  ) : (
                    <EmailDarkWebVerify
                      email={lowerCaseEmailAddress}
                      handleDarkWebLoadingScreen={
                        handleDarkWebSectionLoadingScreen
                      }
                    />
                  )}
                </>
              )}
            </>
          )}
          {/* END DARK_WEB_SECTION */}

          {/* START POTENTIAL_OWNERS_SECTION */}
          <PotentialOwnersV2
            potentialOwners={potentialOwners || []}
            potentialOwnerIndex={reportOptions.potentialOwnerIndex}
            setPotentialOwnerIndex={(index: number) => {
              handleSetReportOptions({ potentialOwnerIndex: index });
            }}
            personName={ownerName || lowerCaseEmailAddress}
            permalink={permalink}
            reportType={reportType}
            reportSections={reportSections}
          />

          {/* END POTENTIAL_OWNERS_SECTION */}

          {/* START PERSONAL_INFO_SECTIONS */}
          <PhoneSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            phoneList={phoneItems}
            permalink={permalink}
          />
          <EmailSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            emailList={emailItems}
            permalink={permalink}
          />
          <AddressHistorySectionV2
            addresses={addressItems}
            personName={ownerName || lowerCaseEmailAddress}
            permalink={permalink}
            realEstate={ownedAssets?.real_estate}
            alternateTitle={alternativeTitle}
          />
          <SocialSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />
          {/* END PERSONAL_INFO_SECTIONS */}
          {/* START ADDITIONAL_INFO_SECTIONS */}
          <JobsSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            jobs={jobs}
            permalink={permalink}
          />
          <EducationSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            educations={educations}
            permalink={permalink}
          />
          <AssetsSectionV2
            personName={ownerName || lowerCaseEmailAddress}
            ownedAssets={ownedAssets}
            permalink={permalink}
          />
          <NotesSectionV2 permalink={permalink} />
          <RelatedReportsSectionV2
            reportType="email"
            personName={ownerName || lowerCaseEmailAddress}
            potentialOwners={report?.data?.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />
          <ReportRatingV2
            report_type={reportType}
            rating={report.data.meta?.rating || null}
          />
          {/* END ADDITIONAL_INFO_SECTIONS */}
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Email Report"
        headerTitle={lowerCaseEmailAddress}
        menuItems={
          navMenuItemsWithPermissions as ReportNavigationMenuProps['menuItems']
        }
      />
      {/* END MOBILE_NAVIGATION */}

      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType="Dark Web"
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
